import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Printer, Save, Eye, QrCode, Store, Phone, MapPin,
  Instagram, MessageCircle, FileText, Type, Loader2
} from "lucide-react";

export default function ReceiptConfigPage() {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restaurant, setRestaurant] = useState({});
  const [config, setConfig] = useState({
    receipt_footer: "Obrigado pela preferencia!",
    show_qr_code: true,
    qr_message: "Escaneie e veja nosso menu",
    qr_size: 6,
    menu_url: "",
    address: "",
    phone: "",
    instagram: "",
    whatsapp_phone: "",
    show_address: true,
    show_phone: true,
    show_cif: true,
    show_social: true,
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const r = await authAxios.get("/restaurant");
      const data = r.data;
      setRestaurant(data);
      const rc = data.receipt_config || {};
      setConfig(prev => ({
        ...prev,
        receipt_footer: rc.receipt_footer || prev.receipt_footer,
        show_qr_code: rc.show_qr_code !== false,
        qr_message: rc.qr_message || prev.qr_message,
        qr_size: rc.qr_size || 6,
        menu_url: rc.menu_url || (data.slug ? `https://kaisosystem.com/menu/${data.slug}` : ""),
        address: rc.address || data.address || "",
        phone: rc.phone || data.phone || "",
        instagram: rc.instagram || data.instagram_url || "",
        whatsapp_phone: rc.whatsapp_phone || "",
        show_address: rc.show_address !== false,
        show_phone: rc.show_phone !== false,
        show_cif: rc.show_cif !== false,
        show_social: rc.show_social !== false,
      }));
    } catch (e) {
      toast.error("Erro ao carregar configuracoes");
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.patch("/restaurant", { receipt_config: config });
      toast.success("Configuracoes do recibo salvas!");
    } catch (e) {
      toast.error("Erro ao salvar");
    }
    setSaving(false);
  };

  const update = (key, value) => setConfig(prev => ({ ...prev, [key]: value }));

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </MainLayout>
    );
  }

  const menuUrl = config.menu_url || (restaurant.slug ? `https://kaisosystem.com/menu/${restaurant.slug}` : "");

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="receipt-config-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6" /> Configuracao do Recibo
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Personalize o recibo termico da sua marca
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 gap-2" data-testid="save-receipt-config-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar
          </Button>
        </div>

        {/* Identidade do Restaurante */}
        <Card data-testid="restaurant-identity-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Store className="w-5 h-5 text-orange-500" /> Identidade no Recibo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-700">
              O <strong>nome</strong>, <strong>logo</strong> e <strong>NIF/CIF</strong> vem das configuracoes gerais do restaurante.
              Aqui voce configura dados extras que aparecem no recibo.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1.5 mb-1.5 text-sm">
                  <MapPin className="w-3.5 h-3.5" /> Endereco no recibo
                </Label>
                <Input
                  value={config.address}
                  onChange={e => update("address", e.target.value)}
                  placeholder="Rua Exemplo, 123 - Cidade"
                  data-testid="receipt-address-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1.5 mb-1.5 text-sm">
                  <Phone className="w-3.5 h-3.5" /> Telefone no recibo
                </Label>
                <Input
                  value={config.phone}
                  onChange={e => update("phone", e.target.value)}
                  placeholder="+351 912 345 678"
                  data-testid="receipt-phone-input"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={config.show_address} onCheckedChange={v => update("show_address", v)} data-testid="show-address-switch" />
                <Label className="text-sm">Mostrar endereco</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={config.show_phone} onCheckedChange={v => update("show_phone", v)} data-testid="show-phone-switch" />
                <Label className="text-sm">Mostrar telefone</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={config.show_cif} onCheckedChange={v => update("show_cif", v)} data-testid="show-cif-switch" />
                <Label className="text-sm">Mostrar NIF/CIF</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* QR Code */}
        <Card data-testid="qr-code-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <QrCode className="w-5 h-5 text-orange-500" /> QR Code do Menu
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Switch checked={config.show_qr_code} onCheckedChange={v => update("show_qr_code", v)} data-testid="show-qr-switch" />
              <Label className="text-sm font-medium">Imprimir QR Code no recibo</Label>
            </div>

            {config.show_qr_code && (
              <>
                <div>
                  <Label className="mb-1.5 text-sm">URL do menu (link que o QR Code abre)</Label>
                  <Input
                    value={config.menu_url}
                    onChange={e => update("menu_url", e.target.value)}
                    placeholder="https://kaisosystem.com/menu/seu-restaurante"
                    data-testid="menu-url-input"
                  />
                  {menuUrl && !config.menu_url && (
                    <p className="text-xs text-gray-400 mt-1">Auto: {menuUrl}</p>
                  )}
                </div>
                <div>
                  <Label className="mb-1.5 text-sm">Mensagem acima do QR Code</Label>
                  <Input
                    value={config.qr_message}
                    onChange={e => update("qr_message", e.target.value)}
                    placeholder="Escaneie e veja nosso menu"
                    data-testid="qr-message-input"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 text-sm">Tamanho do QR Code ({config.qr_size})</Label>
                  <input
                    type="range"
                    min="3"
                    max="10"
                    value={config.qr_size}
                    onChange={e => update("qr_size", parseInt(e.target.value))}
                    className="w-full accent-orange-500"
                    data-testid="qr-size-slider"
                  />
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Pequeno</span>
                    <span>Grande</span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Rodape */}
        <Card data-testid="footer-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Type className="w-5 h-5 text-orange-500" /> Rodape do Recibo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="mb-1.5 text-sm">Mensagem final</Label>
              <Input
                value={config.receipt_footer}
                onChange={e => update("receipt_footer", e.target.value)}
                placeholder="Obrigado pela preferencia!"
                data-testid="receipt-footer-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={config.show_social} onCheckedChange={v => update("show_social", v)} data-testid="show-social-switch" />
              <Label className="text-sm font-medium">Mostrar redes sociais</Label>
            </div>

            {config.show_social && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-1.5 mb-1.5 text-sm">
                    <Instagram className="w-3.5 h-3.5" /> Instagram
                  </Label>
                  <Input
                    value={config.instagram}
                    onChange={e => update("instagram", e.target.value)}
                    placeholder="@seurestaurante"
                    data-testid="receipt-instagram-input"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1.5 mb-1.5 text-sm">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </Label>
                  <Input
                    value={config.whatsapp_phone}
                    onChange={e => update("whatsapp_phone", e.target.value)}
                    placeholder="+351 912 345 678"
                    data-testid="receipt-whatsapp-input"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview */}
        <Card data-testid="receipt-preview-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Eye className="w-5 h-5 text-orange-500" /> Preview do Recibo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 max-w-[320px] mx-auto font-mono text-xs leading-relaxed shadow-md" data-testid="receipt-preview">
              {/* Header */}
              <div className="text-center mb-2">
                <p className="text-sm font-black tracking-wide">{(restaurant.name || "SEU RESTAURANTE").toUpperCase()}</p>
                {config.show_address && config.address && <p className="text-[10px] text-gray-600">{config.address}</p>}
                {config.show_phone && config.phone && <p className="text-[10px] text-gray-600">Tel: {config.phone}</p>}
                {config.show_cif && restaurant.cif && <p className="text-[10px] text-gray-600">NIF: {restaurant.cif}</p>}
              </div>
              <div className="border-t-2 border-black my-1.5" />

              {/* Order info */}
              <p className="font-bold text-[11px]">Pedido #A1B2C3</p>
              <p>Data: {new Date().toLocaleDateString("pt-BR")} {new Date().toLocaleTimeString("pt-BR", {hour:"2-digit",minute:"2-digit"})}</p>
              <p>Cliente: Maria Silva</p>
              <p>Canal: WhatsApp</p>
              <div className="border-t-2 border-black my-1.5" />

              {/* Items */}
              <p className="text-center font-bold text-[10px] tracking-wider">ITENS DO PEDIDO</p>
              <div className="border-t border-gray-400 border-dashed my-1" />
              <div className="space-y-1">
                <div>
                  <p className="font-bold">2x Sushi Mix Especial</p>
                  <p className="text-right">31.80{restaurant.currency === "brl" ? "R$" : "\u20AC"}</p>
                </div>
                <div>
                  <p className="font-bold">1x Temaki Salmao</p>
                  <p className="text-right">8.50{restaurant.currency === "brl" ? "R$" : "\u20AC"}</p>
                  <p className="text-gray-500 text-[9px]">  * Sem wasabi</p>
                </div>
                <div>
                  <p className="font-bold">2x Coca-Cola 330ml</p>
                  <p className="text-right">5.00{restaurant.currency === "brl" ? "R$" : "\u20AC"}</p>
                </div>
              </div>
              <div className="border-t border-gray-400 border-dashed my-1" />

              {/* Totals */}
              <div className="flex justify-between"><span>Subtotal:</span><span>45.30{restaurant.currency === "brl" ? "R$" : "\u20AC"}</span></div>
              <div className="flex justify-between"><span>Taxa entrega:</span><span>3.50{restaurant.currency === "brl" ? "R$" : "\u20AC"}</span></div>
              <div className="border-t-2 border-black my-1.5" />
              <div className="flex justify-between text-sm font-black">
                <span>TOTAL</span><span>48.80{restaurant.currency === "brl" ? "R$" : "\u20AC"}</span>
              </div>
              <div className="border-t-2 border-black my-1.5" />
              <div className="flex justify-between font-bold"><span>Pagamento:</span><span>CARTAO</span></div>

              {/* QR Code */}
              {config.show_qr_code && (
                <div className="text-center mt-3 mb-2">
                  <div className="border-t border-gray-400 border-dashed my-1.5" />
                  <p className="text-[10px] text-gray-600 mb-2">{config.qr_message || "Escaneie e veja nosso menu"}</p>
                  <div className="inline-block bg-gray-100 border border-gray-300 rounded p-2">
                    <QrCode className="w-16 h-16 text-gray-700 mx-auto" />
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="border-t-2 border-black my-1.5" />
              <div className="text-center space-y-0.5">
                <p className="text-[10px]">{config.receipt_footer}</p>
                {config.show_social && config.instagram && (
                  <p className="text-[9px] text-gray-500">{config.instagram}</p>
                )}
                {config.show_social && config.whatsapp_phone && (
                  <p className="text-[9px] text-gray-500">WhatsApp: {config.whatsapp_phone}</p>
                )}
              </div>
            </div>
            <p className="text-center text-xs text-gray-400 mt-3">
              Representacao visual. O recibo real usa ESC/POS com formatacao termica.
            </p>
          </CardContent>
        </Card>

        {/* Save footer */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 gap-2" data-testid="save-receipt-config-bottom-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar Configuracoes
          </Button>
        </div>
      </div>
    </MainLayout>
  );
}
